import React, { useRef, useState } from "react";
import {
  ModalStyled,
  ModalBackgroundStyled,
} from "../../../../components/common/ModalComponent/styles";
import { releasesApi } from "../../api";
import {
  AdvancedLink,
  ClearFileButton,
  CloseButton,
  ComponentRow,
  ComponentRowName,
  ComponentRowVersion,
  ComponentTable,
  ComponentTypeBadge,
  DoneButton,
  DropHint,
  DropLink,
  DropText,
  DropZone,
  ErrorBlock,
  ErrorHint,
  ErrorList,
  ErrorTitle,
  ExampleLink,
  FileName,
  ModalBox,
  ModalSubtitle,
  ModalTitle,
  SelectedFile,
  StatusBadge,
  SummaryBlock,
  SummaryTitle,
  UploadButton,
} from "../ImportReleaseModal/styles";

interface BulkVersionModalProps {
  onClose: () => void;
  onComplete?: () => void;
}

const ACCEPTED = [".yaml", ".yml", ".json"];

function isAccepted(file: File): boolean {
  return ACCEPTED.some((ext) => file.name.toLowerCase().endsWith(ext));
}

export const BulkVersionModal: React.FC<BulkVersionModalProps> = ({
  onClose,
  onComplete,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{
    message: string;
    hint?: string;
    missing?: { name: string; type: string }[];
    extra?: string[];
    details?: { name: string; error: string }[];
  } | null>(null);
  const [summary, setSummary] = useState<{
    totalComponents: number;
    created: { name: string; versionNumber: string; type: string }[];
    reused: { name: string; versionNumber: string; type: string }[];
  } | null>(null);

  function selectFile(f: File) {
    if (!isAccepted(f)) {
      setError({ message: "Only .yaml, .yml, or .json files are accepted." });
      return;
    }
    setFile(f);
    setError(null);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) selectFile(dropped);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (picked) selectFile(picked);
    e.target.value = "";
  }

  async function handleUpload() {
    if (!file || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await releasesApi.importVersionsFromManifest(file);
      setSummary(result.summary);
      if (result.summary.created.length > 0) onComplete?.();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      if (data) {
        setError({
          message: (data.error as string) || "An unexpected error occurred.",
          hint: data.hint as string | undefined,
          missing: data.missingComponents as unknown as
            | { name: string; type: string }[]
            | undefined,
          extra: data.extraComponents as string[] | undefined,
          details: data.details as
            | { name: string; error: string }[]
            | undefined,
        });
      } else {
        setError({ message: "Network error — please try again." });
      }
    } finally {
      setLoading(false);
    }
  }

  const allComponents = summary
    ? [
        ...summary.created.map((c) => ({ ...c, status: "created" as const })),
        ...summary.reused.map((c) => ({ ...c, status: "found" as const })),
      ]
    : [];

  const content = summary ? (
    <>
      <ModalTitle>Versions Created</ModalTitle>
      <SummaryBlock>
        <SummaryTitle>
          {summary.totalComponents} component version(s)
        </SummaryTitle>
        <ComponentTable>
          {allComponents.map((c) => (
            <ComponentRow key={c.name}>
              <StatusBadge $status={c.status}>
                {c.status === "created" ? "Created" : "Found"}
              </StatusBadge>
              <ComponentRowName>{c.name}</ComponentRowName>
              <ComponentRowVersion>v{c.versionNumber}</ComponentRowVersion>
              <ComponentTypeBadge $type={c.type}>{c.type}</ComponentTypeBadge>
            </ComponentRow>
          ))}
        </ComponentTable>
      </SummaryBlock>
      <DoneButton onClick={onClose}>Close</DoneButton>
    </>
  ) : (
    <>
      <div>
        <ModalTitle>Bulk Create Versions</ModalTitle>
        <ModalSubtitle>
          Upload a YAML or JSON file listing component versions to create or
          reuse. Only the <code>components</code> list is required — no release
          fields needed.{" "}
          <ExampleLink
            onClick={() => {
              const a = document.createElement("a");
              a.href =
                "data:text/yaml;charset=utf-8," +
                encodeURIComponent(
                  "components:\n" +
                    "  # Firmware - s3_uri/sha256_hash/firmware_version needed only when creating a new version.\n" +
                    "  - name: ecu_main\n" +
                    "    version: 1.2.3\n" +
                    "    firmware_key: esp32c3\n" +
                    "    s3_uri: s3://cyngn-deployment-ota/artifacts/com.cyngn.ecu_main/1.2.3/ecu_main.bin\n" +
                    "    sha256_hash: a3f1c2d4e5b6...\n" +
                    "    firmware_version: 2.3.6.3\n" +
                    "    description: Optional free-text note for this version\n" +
                    "    tags: [stable, june-sprint]\n" +
                    "\n" +
                    "  # Container - ccm_image_tag needed only when creating a new version.\n" +
                    "  - name: cynsrv_ouster_ros\n" +
                    "    version: 1.0.5\n" +
                    "    ccm_image_tag: CS80d92ea3\n" +
                    "    ccm_service_name: cynsrv_ouster_ros\n" +
                    "    description: Optional free-text note for this version\n" +
                    "    tags: [stable, june-sprint]\n" +
                    "\n" +
                    "  # Host - host_package_version needed only when creating a new version.\n" +
                    "  - name: cyngn-platform\n" +
                    "    version: 1.3.0\n" +
                    "    host_package_version: 1.3.0~rc7\n" +
                    "    description: Optional free-text note for this version\n" +
                    "    tags: [stable, june-sprint]\n"
                );
              a.download = "versions-manifest-example.yaml";
              a.click();
            }}
          >
            Download example
          </ExampleLink>
        </ModalSubtitle>
      </div>

      {!file ? (
        <DropZone
          isDragging={isDragging}
          onClick={() => fileRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <DropText>
            Drop your manifest here, or <DropLink>click to browse</DropLink>
          </DropText>
          <DropHint>Supported formats: .yaml, .yml, .json</DropHint>
        </DropZone>
      ) : (
        <SelectedFile>
          <FileName>{file.name}</FileName>
          <ClearFileButton
            onClick={() => setFile(null)}
            aria-label="Remove file"
          >
            ×
          </ClearFileButton>
        </SelectedFile>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED.join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {error && (
        <ErrorBlock>
          <ErrorTitle>{error.message}</ErrorTitle>
          {error.hint && <ErrorHint>{error.hint}</ErrorHint>}
          {error.missing && error.missing.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Missing from manifest ({error.missing.length}):
              </div>
              <ErrorList>
                {error.missing.map((c) => (
                  <li key={c.name}>
                    {c.name}
                    <ComponentTypeBadge $type={c.type}>
                      {c.type}
                    </ComponentTypeBadge>
                  </li>
                ))}
              </ErrorList>
            </>
          )}
          {error.extra && error.extra.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Not registered for this vehicle type ({error.extra.length}):
              </div>
              <ErrorList>
                {error.extra.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ErrorList>
            </>
          )}
          {error.details && error.details.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Component errors ({error.details.length}):
              </div>
              <ErrorList>
                {error.details.map((d, i) => (
                  <li key={i}>
                    <strong>{d.name}</strong>: {d.error}
                  </li>
                ))}
              </ErrorList>
            </>
          )}
        </ErrorBlock>
      )}

      <UploadButton onClick={handleUpload} disabled={!file || loading}>
        {loading ? "Creating versions…" : "Upload & Create Versions"}
      </UploadButton>

      <AdvancedLink onClick={onClose}>Cancel</AdvancedLink>
    </>
  );

  return (
    <ModalStyled>
      <ModalBackgroundStyled onClick={onClose} />
      <ModalBox>
        <CloseButton onClick={onClose} aria-label="Close">
          ×
        </CloseButton>
        {content}
      </ModalBox>
    </ModalStyled>
  );
};
